import { z } from 'zod';

/*
MARKS
*/

const BoldMark = z
  .object({
    type: z.literal('bold'),
  })
  .describe('A bold text formatting mark.');

const ItalicMark = z
  .object({
    type: z.literal('italic'),
  })
  .describe('An italic text formatting mark.');

const UnderlineMark = z
  .object({
    type: z.literal('underline'),
  })
  .describe('An underline text formatting mark.');

const StrikeMark = z
  .object({
    type: z.literal('strike'),
  })
  .describe('A strikethrough text formatting mark.');

const CodeMark = z
  .object({
    type: z.literal('code'),
  })
  .describe('An inline code formatting mark.');

const LinkMark = z
  .object({
    type: z.literal('link'),
    attrs: z
      .object({
        href: z.string().describe('The absolute URL for the link target.'),
        target: z
          .enum(['_blank', '_self'])
          .nullable()
          .describe(
            'The link target. Use "_blank" to open in a new tab, "_self" for the same tab, or null if unspecified.',
          ),
      })
      .describe('Attributes for a link mark.'),
  })
  .describe('A hyperlink formatting mark applied to inline text.');

const TextStyleMark = z
  .object({
    type: z.literal('textStyle'),
    attrs: z
      .object({
        color: z
          .string()
          .min(1)
          .nullable()
          .describe('The CSS text color to apply to this inline text span.'),
      })
      .describe('Attributes for an inline text style mark.'),
  })
  .describe(
    'An inline text styling mark used for colorized text spans.',
  );

const MarkSchema = z
  .discriminatedUnion('type', [
    BoldMark,
    ItalicMark,
    UnderlineMark,
    StrikeMark,
    CodeMark,
    LinkMark,
    TextStyleMark,
  ])
  .describe('A text formatting mark that can be applied to a text node.');

/*
INLINE NODES
*/

const TextNode = z
  .object({
    type: z.literal('text'),
    text: z
      .string()
      .min(1)
      .describe('The raw text content for this text node.'),
    marks: z
      .array(MarkSchema)
      .max(8)
      .describe('Optional formatting marks applied to this text node, if any.'),
  })
  .describe('A text node containing visible inline text.');

const HardBreakNode = z
  .object({
    type: z.literal('hardBreak'),
  })
  .describe('A hard line break inside inline content.');

const InlineNode = z
  .discriminatedUnion('type', [TextNode, HardBreakNode])
  .describe(
    'An inline node that can appear inside paragraphs, headings, and similar blocks.',
  );

/*
BLOCK NODES
*/

const ParagraphNode = z
  .object({
    type: z.literal('paragraph'),
    attrs: z
      .object({
        class: z
          .string()
          .min(1)
          .nullable()
          .describe('An optional CSS class name applied to the paragraph.'),
      })
      .describe('Attributes for a paragraph block.'),
    content: z
      .array(InlineNode)
      .min(1)
      .describe('The inline content contained inside the paragraph.'),
  })
  .describe('A paragraph block containing one or more inline nodes.');

const HeadingNode = z
  .object({
    type: z.literal('heading'),
    attrs: z
      .object({
        level: z
          .number()
          .int()
          .min(1)
          .max(6)
          .describe('The heading level, from 1 through 6.'),
      })
      .describe('Attributes for a heading block.'),
    content: z
      .array(InlineNode)
      .min(1)
      .describe('The inline content contained inside the heading.'),
  })
  .describe('A heading block with a level from 1 to 6.');

const HorizontalRuleNode = z
  .object({
    type: z.literal('horizontalRule'),
  })
  .describe('A horizontal rule block used as a section divider.');

const BlockquoteNode = z
  .object({
    type: z.literal('blockquote'),
    content: z
      .array(ParagraphNode)
      .min(1)
      .describe('One or more paragraph nodes contained inside the blockquote.'),
  })
  .describe('A blockquote containing one or more paragraphs.');

/*
LISTS
*/

const ListItemNode = z
  .object({
    type: z.literal('listItem'),
    content: z
      .array(ParagraphNode)
      .min(1)
      .describe('One or more paragraph nodes contained inside the list item.'),
  })
  .describe('A single list item containing one or more paragraphs.');

const BulletListNode = z
  .object({
    type: z.literal('bulletList'),
    content: z
      .array(ListItemNode)
      .min(1)
      .describe('The ordered sequence of list items in the bullet list.'),
  })
  .describe('A bulleted list containing one or more list items.');

const OrderedListNode = z
  .object({
    type: z.literal('orderedList'),
    attrs: z
      .object({
        start: z
          .number()
          .int()
          .min(1)
          .nullable()
          .describe(
            'The optional starting number for the ordered list, or null if it should start from the default.',
          ),
      })
      .describe('Attributes for an ordered list.'),
    content: z
      .array(ListItemNode)
      .min(1)
      .describe('The ordered sequence of list items in the ordered list.'),
  })
  .describe('A numbered list containing one or more list items.');

/*
BLOCK UNION
*/

const BlockNode = z
  .discriminatedUnion('type', [
    ParagraphNode,
    HeadingNode,
    BulletListNode,
    OrderedListNode,
    BlockquoteNode,
    HorizontalRuleNode,
  ])
  .describe('A top-level block node that can appear in the document content.');

/*
DOCUMENT ROOT
*/

export const ProsemirrorOutput = z
  .object({
    type: z.literal('doc'),
    content: z
      .array(BlockNode)
      .min(1)
      .describe('The ordered top-level block nodes in the document.'),
  })
  .describe('A structured Tiptap-style rich text document.');

export type ProsemirrorOutput = z.infer<typeof ProsemirrorOutput>;
